"use client";

import { useState, useEffect, Suspense, lazy } from "react";

import { translations, Lang } from "./i18n";
import { buildRegisterUrl, captureAttribution } from "./lib/tracking-url";
import { trackSchedule } from "./lib/pixel";
import { ctaSource, demoUrl, scrollToBooking, scrollToForm } from "./lib/cta";
import { mountCalendly, warmCalendly } from "./lib/calendly";
import CookieConsent from "./components/CookieConsent";
import CookieConsentBlocking from "./components/CookieConsentBlocking";
import { MetaPixel } from "./components/MetaPixel";
import { GoogleTagManager } from "./components/GoogleTagManager";
import { Clarity } from "./components/Clarity";
import { Leadinfo } from "./components/Leadinfo";
import { PostHog } from "./components/PostHog";
// Route-level code-splitting: lazy-load pages so unused routes
// (e.g. data-security, demo) don't bloat the initial /blogs bundle.
const ImprintPage = lazy(() => import("./pages/Imprint"));
const DataSecurityPage = lazy(() => import("./pages/DataSecurity"));
const InsightsPage = lazy(() => import("./pages/Insights"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const ThankYouPage = lazy(() => import("./pages/ThankYou"));
const DemoPage = lazy(() => import("./pages/Demo"));
const DemoVideoPage = lazy(() => import("./pages/DemoVideo"));
const MipimPage = lazy(() => import("./pages/Mipim.tsx"));
const FAQPage = lazy(() => import("./pages/FAQ"));

// Landing and shared layout components: keep as regular imports so
// home rendering stays simple and reliable.
import BausignalePage from "./pages/Bausignale";
import VorsprungPage from "./pages/Vorsprung";
import {
  ChallengeSolution,
  Benefits,
  HowItWorks,
  LiveFeed,
  Features,
  Testimonials,
  Pricing,
  PricingTable,
  CTA,
  Navbar,
  MouseFollower,
  Footer,
} from "./components/landing";

// Support deployments under a sub-path (e.g. GitHub Pages project sites).
// Vite exposes this as BASE_URL when `base` is configured.
const basePath =
  import.meta.env.BASE_URL && import.meta.env.BASE_URL !== "/"
    ? import.meta.env.BASE_URL.replace(/\/$/, "")
    : "";

const stripBase = (pathname: string) => {
  if (!basePath) return pathname || "/";
  if (!pathname) return "/";
  if (pathname === basePath || pathname === `${basePath}/`) return "/";
  if (pathname.startsWith(`${basePath}/`))
    return pathname.slice(basePath.length) || "/";
  return pathname;
};

const normalizeRoute = (path: string) => {
  if (!path) return "/";
  const withLeading = path.startsWith("/") ? path : `/${path}`;
  // Drop trailing slashes except for root
  const trimmed = withLeading.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
};

const withBase = (path: string) => {
  const p = path.startsWith("/") ? path : `/${path}`;
  return basePath ? `${basePath}${p}` : p;
};

function App() {
  // Minimal SPA routing: render imprint/data-security within the app layer
  const [route, setRoute] = useState<string>(() => {
    // Handle SPA redirect from 404.html
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect");
    if (redirect) {
      // Clean URL and return the redirect path (normalized for basePath deployments)
      const url = new URL(redirect, window.location.origin);
      const internal = normalizeRoute(stripBase(url.pathname || "/"));
      window.history.replaceState(
        null,
        "",
        `${withBase(internal)}${url.search || ""}${url.hash || ""}`,
      );
      // The 404 hop hid the original query behind ?redirect=…; now that the
      // UTMs are restored to the address bar, capture them (module-scope
      // captureAttribution in main.tsx ran too early and saw none).
      captureAttribution();
      return internal;
    }
    const raw = stripBase(window.location.pathname || "/");
    const normalized = normalizeRoute(raw);
    if (normalized !== raw) {
      window.history.replaceState(
        null,
        "",
        `${withBase(normalized)}${window.location.search || ""}${
          window.location.hash || ""
        }`,
      );
    }
    return normalized;
  });

  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window === "undefined") return "de";
    const stored = window.localStorage.getItem("neosearch-lang");
    return stored === "en" ? "en" : "de";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("neosearch-lang", lang);
  }, [lang]);

  const t = translations[lang];

  useEffect(() => {
    const onPop = () =>
      setRoute(() => {
        const raw = stripBase(window.location.pathname || "/");
        const normalized = normalizeRoute(raw);
        if (normalized !== raw) {
          window.history.replaceState(
            null,
            "",
            `${withBase(normalized)}${window.location.search || ""}${
              window.location.hash || ""
            }`,
          );
        }
        return normalized;
      });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Accepts a query/hash on `path` (e.g. "/demo?utm_source=…") so forwarded
  // attribution lands in the address bar; only the pathname selects the route.
  const navigate = (path: string) => {
    const url = new URL(path, window.location.origin);
    const internal = normalizeRoute(stripBase(url.pathname || "/"));
    const target = `${withBase(internal)}${url.search}${url.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (target === current) return;
    history.pushState(null, "", target);
    setRoute(internal);
    window.scrollTo(0, 0);
  };

  const [visibleSections, setVisibleSections] = useState<Set<string>>(
    new Set(),
  );
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Booking is one click away from these pages, so warm /demo's slowest part
  // here. Calendly is a third party, so it waits for a consent decision.
  useEffect(() => {
    const isDemoFunnel =
      route === "/" ||
      route === "/vorsprung" ||
      route === "/vorsprung-demo" ||
      route === "/bausignale";
    if (!isDemoFunnel) return;

    void import("./pages/Demo");

    const warmIfConsented = () => {
      if (!localStorage.getItem("cookie-consent")) return false;
      warmCalendly();
      return true;
    };
    if (warmIfConsented()) return;

    window.addEventListener("cookie-consent-changed", warmIfConsented);
    return () =>
      window.removeEventListener("cookie-consent-changed", warmIfConsented);
  }, [route]);

  // Build the scheduler on the first reach toward a demo CTA. Doing it on load
  // instead would hand Calendly's year-long cookie to every visitor, including
  // the ones who never open the booking page.
  useEffect(() => {
    const onIntent = (e: Event) => {
      const el = e.target as Element | null;
      if (!el?.closest?.('a[href^="/demo"]')) return;
      if (!localStorage.getItem("cookie-consent")) return;
      mountCalendly();
    };
    document.addEventListener("pointerover", onIntent);
    document.addEventListener("touchstart", onIntent, { passive: true });
    return () => {
      document.removeEventListener("pointerover", onIntent);
      document.removeEventListener("touchstart", onIntent);
    };
  }, []);

  useEffect(() => {
    const el = document.querySelector('meta[name="robots"]');
    if (!el) return;
    el.setAttribute(
      "content",
      // `/vorsprung` and `/vorsprung-demo` still serve the light landing (the
      // links are in active use) but are noindex so they aren't crawled as
      // duplicate content of canonical `/`. `/anfrage-v1` is the kept-as-backup
      // previous `/anfrage` layout, likewise noindex.
      route === "/bausignale" ||
      route === "/demo-video" ||
      route === "/vorsprung" ||
      route === "/vorsprung-demo" ||
      route === "/anfrage-v1"
        ? "noindex, nofollow"
        : "index, follow",
    );
  }, [route]);

  // Self-referential canonical per route. index.html ships a canonical of `/`;
  // without this every prerendered route (e.g. /demo, /insights) would claim
  // the homepage as its canonical and risk being dropped from the index. The
  // duplicate light-landing routes fold back to `/`.
  useEffect(() => {
    const SITE_URL = "https://neosearch.neobim.ai";
    const foldsToHome =
      route === "/vorsprung" ||
      route === "/vorsprung-demo" ||
      route === "/bausignale";
    const path =
      route === "/" || foldsToHome
        ? "/"
        : route === "/anfrage-v1"
          ? "/anfrage"
          : route;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", `${SITE_URL}${path}`);
  }, [route]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      // Pre-trigger sections before they enter the viewport so fast-scrolling
      // users don't see opacity-0 sections flash by. rootMargin extends the
      // viewport's bottom edge by 300px (i.e. fire when an element is still
      // 300px below the fold).
      { threshold: 0, rootMargin: "0px 0px 300px 0px" },
    );

    document
      .querySelectorAll("[data-animate]")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isVisible = (id: string) => visibleSections.has(id);

  const pageFallback = (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
      <span className="text-sm text-slate-300">Lädt…</span>
    </div>
  );

  // route-specific rendering: imprint and data-security handled in-app.
  // /impressum and /datenschutz are German-language URL aliases — the site is
  // targeted at German municipalities, and TMG §5 / DSGVO compliance pages
  // are typically reached at those paths, so we accept both.
  if (route === "/imprint" || route === "/impressum") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <ImprintPage onBack={() => navigate("/")} />
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/data-security" || route === "/datenschutz") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <DataSecurityPage onBack={() => navigate("/")} />
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/insights") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <InsightsPage />
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/thank-you") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <ThankYouPage onBack={() => navigate("/")} />
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/demo") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <div className="bg-white text-slate-900 relative overflow-x-hidden">
            {/* Logo goes home. It must never point at /bausignale — that page
                is intentionally unreachable from anywhere on the site. */}
            <Navbar
              lang={lang}
              onLangChange={setLang}
              t={t.nav}
              theme="light"
              logoHref="/"
              primaryCtaLabel={`${t.demoBooking.scrollCta} ↓`}
              primaryCtaHref="#booking"
              onPrimaryCtaClick={(e) => {
                e.preventDefault();
                scrollToBooking();
              }}
            />
            <main>
              <DemoPage t={t.demoBooking} proofT={t.proof} />
            </main>
            <Footer navigate={navigate} t={t.footer} lang={lang} />
          </div>
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/demo-video") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <div className="bg-slate-950 text-white overflow-hidden relative">
            <MouseFollower x={mousePosition.x} y={mousePosition.y} />
            <Navbar lang={lang} onLangChange={setLang} t={t.nav} />
            <main>
              <DemoVideoPage t={t} />
            </main>
            <Footer navigate={navigate} t={t.footer} lang={lang} />
          </div>
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/mipim") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <MipimPage
            t={t.mipim}
            lang={lang}
            onLangChange={setLang}
            navigate={navigate}
            navT={t.nav}
            footerT={t.footer}
          />
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/cookie-policy") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <CookiePolicy />
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  // Form-funnel twin of `/vorsprung`: every CTA scrolls to the inline lead form
  // instead of opening the app or booking a call. `/anfrage` is the lean ad page
  // (form last); `/anfrage-v1` keeps the previous full page (form in the hero).
  if (route === "/anfrage" || route === "/anfrage-v1") {
    const classic = route === "/anfrage-v1";
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <div className="bg-white text-slate-900 relative overflow-x-hidden">
            <Navbar
              lang={lang}
              onLangChange={setLang}
              t={t.nav}
              theme="light"
              logoHref={route}
              links={[]}
              compactMobile
              primaryCtaLabel={t.vorsprungForm.ctaPrimary}
              compactMobileCtaLabel={t.vorsprungForm.ctaPrimaryShort}
              primaryCtaHref="#anfrage-form"
              onPrimaryCtaClick={(e) => {
                e.preventDefault();
                scrollToForm();
              }}
            />
            <main>
              <VorsprungPage
                proofT={t.proof}
                navigate={navigate}
                t={{
                  ...t.vorsprung,
                  hero: {
                    ...t.vorsprung.hero,
                    ctaPrimary: t.vorsprungForm.ctaPrimary,
                    ctaNote: t.vorsprungForm.ctaNote,
                  },
                  timeline: {
                    ...t.vorsprung.timeline,
                    cta: t.vorsprungForm.ctaPrimary,
                  },
                  digest: {
                    ...t.vorsprung.digest,
                    cta: t.vorsprungForm.ctaPrimary,
                  },
                  monitor: {
                    ...t.vorsprung.monitor,
                    cta: t.vorsprungForm.ctaPrimary,
                  },
                  footerCta: {
                    ...t.vorsprung.footerCta,
                    cta: t.vorsprungForm.ctaPrimary,
                    ctaNote: t.vorsprungForm.ctaNote,
                  },
                }}
                lang={lang}
                variant="form"
                formT={t.anfrage}
                formLayout={classic ? "classic" : "lean"}
                pricingSlot={
                  classic && (
                    <PricingTable
                      isVisible={isVisible}
                      t={{
                        ...t.pricingTable,
                        subheading: t.vorsprungForm.ctaNote,
                        plans: t.pricingTable.plans.map((plan) => ({
                          ...plan,
                          cta: plan.contact ? plan.cta : t.vorsprungForm.ctaPrimary,
                        })),
                      }}
                      theme="light"
                      ctaOverride={{
                        href: "#anfrage-form",
                        onClick: (e) => {
                          e.preventDefault();
                          scrollToForm();
                        },
                      }}
                    />
                  )
                }
              />
            </main>
            <Footer navigate={navigate} t={t.footer} lang={lang} />
          </div>
        </Suspense>
        <CookieConsentBlocking lang={lang} />
      </>
    );
  }

  // Demo-funnel twin of `/vorsprung`: same page, but every CTA books a call
  // instead of opening the app's start page.
  if (route === "/vorsprung-demo") {
    const demoHref = demoUrl();
    const goToDemo = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      trackSchedule({ source: ctaSource("demo") });
      navigate(demoHref);
    };
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <div className="bg-white text-slate-900 relative overflow-x-hidden">
            <Navbar
              lang={lang}
              onLangChange={setLang}
              t={t.nav}
              theme="light"
              logoHref="/vorsprung-demo"
              links={[]}
              compactMobile
              primaryCtaLabel={t.vorsprungDemo.ctaPrimary}
              compactMobileCtaLabel={t.vorsprungDemo.ctaPrimaryShort}
              primaryCtaHref={demoHref}
              onPrimaryCtaClick={goToDemo}
            />
            <main>
              <VorsprungPage
                proofT={t.proof}
                navigate={navigate}
                t={{
                  ...t.vorsprung,
                  hero: {
                    ...t.vorsprung.hero,
                    sub: t.vorsprungDemo.heroSub,
                    ctaPrimary: t.vorsprungDemo.ctaPrimary,
                    ctaNote: t.vorsprungDemo.ctaNote,
                  },
                  timeline: {
                    ...t.vorsprung.timeline,
                    headLead: t.vorsprungDemo.timelineHeadLead,
                    headAccent: t.vorsprungDemo.timelineHeadAccent,
                    lead: t.vorsprungDemo.timelineLead,
                    cta: t.vorsprungDemo.ctaPrimary,
                    steps: t.vorsprungDemo.timelineSteps.map((label) => ({
                      label,
                      sub: "",
                    })),
                  },
                  digest: {
                    ...t.vorsprung.digest,
                    cta: t.vorsprungDemo.ctaPrimary,
                    hint: t.vorsprungDemo.inlineLink,
                    sourceLabel: t.vorsprungDemo.inlineLink,
                  },
                  monitor: {
                    ...t.vorsprung.monitor,
                    cta: t.vorsprungDemo.ctaPrimary,
                  },
                  footerCta: {
                    ...t.vorsprung.footerCta,
                    cta: t.vorsprungDemo.ctaPrimary,
                    ctaNote: t.vorsprungDemo.ctaNote,
                  },
                }}
                lang={lang}
                variant="demo"
                pricingSlot={
                  <PricingTable
                    isVisible={isVisible}
                    t={{
                      ...t.pricingTable,
                      subheading: t.vorsprungDemo.pricingNote,
                      plans: t.pricingTable.plans.map((plan) => ({
                        ...plan,
                        cta: plan.contact
                          ? plan.cta
                          : t.vorsprungDemo.ctaPrimary,
                      })),
                    }}
                    theme="light"
                    ctaOverride={{ href: demoHref, onClick: goToDemo }}
                  />
                }
              />
            </main>
            <Footer navigate={navigate} t={t.footer} lang={lang} />
          </div>
        </Suspense>
        <CookieConsentBlocking lang={lang} />
      </>
    );
  }

  if (route === "/vorsprung") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <div className="bg-white text-slate-900 relative overflow-x-hidden">
            <Navbar
              lang={lang}
              onLangChange={setLang}
              t={t.nav}
              theme="light"
              logoHref="/vorsprung"
              links={[]}
              showLogin
              compactMobile
              primaryCtaLabel={t.vorsprung.hero.ctaPrimary}
              primaryCtaHref={buildRegisterUrl("https://app.neosearch.neobim.ai/start")}
            />
            <main>
              <VorsprungPage
                proofT={t.proof}
                navigate={navigate}
                t={t.vorsprung}
                lang={lang}
                pricingSlot={
                  <PricingTable
                    isVisible={isVisible}
                    t={{
                      ...t.pricingTable,
                      subheading: t.vorsprung.hero.ctaNote,
                      plans: t.pricingTable.plans.map((plan) => ({
                        ...plan,
                        cta: plan.contact
                          ? plan.cta
                          : t.vorsprung.hero.ctaPrimary,
                      })),
                    }}
                    theme="light"
                  />
                }
              />
            </main>
            <Footer navigate={navigate} t={t.footer} lang={lang} />
          </div>
        </Suspense>
        <CookieConsentBlocking lang={lang} />
      </>
    );
  }

  if (route === "/bausignale") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <div className="bg-slate-950 text-white overflow-hidden relative">
            <MouseFollower x={mousePosition.x} y={mousePosition.y} />
            <Navbar lang={lang} onLangChange={setLang} t={t.nav} />
            <main>
              <BausignalePage navigate={navigate} t={t.bausignale} />
              <ChallengeSolution isVisible={isVisible} t={t.problems} />
              <Benefits isVisible={isVisible} t={t.benefits} />
              <HowItWorks isVisible={isVisible} t={t.how} />
              <LiveFeed isVisible={isVisible} t={t.liveFeed} />
              <Features isVisible={isVisible} t={t.features} />
              <Testimonials isVisible={isVisible} t={t.testimonials} />
              <Pricing isVisible={isVisible} t={t.pricing} />
              <CTA isVisible={isVisible} t={t.cta} />
            </main>
            <Footer navigate={navigate} t={t.footer} lang={lang} />
          </div>
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  if (route === "/faq") {
    return (
      <>
        <MetaPixel />
        <GoogleTagManager />
        <Clarity />
        <Leadinfo />
        <PostHog />
        <Suspense fallback={pageFallback}>
          <FAQPage onBack={() => navigate("/")} />
        </Suspense>
        <CookieConsent lang={lang} />
      </>
    );
  }

  // Canonical home `/` — the white, value-first landing (formerly `/vorsprung`).
  // The dark home was retired here; its section components stay imported because
  // `/bausignale` still renders them.
  return (
    <>
      <MetaPixel />
      <GoogleTagManager />
      <Clarity />
      <Leadinfo />
      <PostHog />
      <Suspense fallback={pageFallback}>
        <div className="bg-white text-slate-900 relative overflow-x-hidden">
          <Navbar
            lang={lang}
            onLangChange={setLang}
            t={t.nav}
            theme="light"
            logoHref="/"
            links={[]}
            showLogin
            compactMobile
            primaryCtaLabel={t.vorsprung.hero.ctaPrimary}
            primaryCtaHref={buildRegisterUrl("https://app.neosearch.neobim.ai/start")}
          />
          <main>
            <VorsprungPage
              proofT={t.proof}
              navigate={navigate}
              t={t.vorsprung}
              lang={lang}
              pricingSlot={
                <PricingTable
                  isVisible={isVisible}
                  t={{
                    ...t.pricingTable,
                    subheading: t.vorsprung.hero.ctaNote,
                    plans: t.pricingTable.plans.map((plan) => ({
                      ...plan,
                      cta: plan.contact
                        ? plan.cta
                        : t.vorsprung.hero.ctaPrimary,
                    })),
                  }}
                  theme="light"
                />
              }
            />
          </main>
          <Footer navigate={navigate} t={t.footer} lang={lang} />
        </div>
      </Suspense>
      <CookieConsentBlocking lang={lang} />
    </>
  );
}

export default App;
